import { ConditionOperator } from "@/types/enums/question-conditions";
import { type QuestionCondition, QuestionType } from "@/types/forms/question";

/**
 * Shared conditional-visibility evaluator for form questions and note-template
 * questions/sections.
 *
 * Lifted out of `dynamic-block.tsx` so forms and notes can never drift apart on
 * what "Equals" means. Forms keep their react-hook-form plumbing and notes read
 * from their Zustand answer map; both just supply `getValue`.
 *
 * Conditions are ANDed and exactly one level deep, and an unrecognised operator
 * resolves to visible.
 *
 * This has a server-side twin in `decoda/services/note_conditions.py`. The two
 * must agree on every operator -- a note evaluated one way in the browser and
 * another way on the server renders differently in the editor than it does in
 * the patient portal. Keep them in step.
 */

export interface ConditionEvalContext {
  /** Current value of the trigger question, in whatever shape the surface stores it. */
  getValue: (dependentQuestionId: string) => unknown;
  /** Only used for the TOGGLE boolean -> "Yes"/"No" coercion. */
  getQuestionType: (dependentQuestionId: string) => QuestionType | undefined;
  /**
   * When true, a condition whose trigger question can't be resolved at all --
   * `getQuestionType` returns undefined, whatever `getValue` still holds --
   * evaluates to VISIBLE rather than hidden.
   *
   * Notes set this: a note renders questions that already have answers, and a
   * trigger may have been archived or recreated since the note was written.
   * Failing closed there would silently drop charted clinical content from a
   * record. Forms leave it false to preserve their existing behaviour exactly.
   */
  failOpenOnUnknownDependency?: boolean;
  /**
   * "Today" for the YEARS_AGO operators, as `YYYY-MM-DD`.
   *
   * Defaults to the browser's local calendar date. Note surfaces must pass the
   * note's own UTC calendar day instead -- `noteEvaluationDate` in
   * `note-utils.ts` -- because that is what the server pins these rules to:
   * `note.created_date.date()`, and `created_date` is stored and read back as
   * UTC. Not the tenant's local date, and not today's. Two reasons, and both
   * matter:
   *
   * * A signed note must not change what it shows as real time passes.
   * * The provider's editor and the patient portal have to land on the same
   *   side of a year boundary. A note written at 19:05 in America/Chicago is
   *   already the next day in UTC; evaluating it tenant-locally in the browser
   *   while the portal evaluates it in UTC is exactly how one surface comes to
   *   hide content the other shows.
   */
  evaluationDate?: string;
}

/**
 * A date with no time and no timezone -- the only thing a "years ago" rule means.
 */
interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The years the server can represent: `datetime.date` spans 1 to 9999 and raises
 * outside it. Anything the browser accepts beyond this range is a date the
 * server cannot build, so the two would disagree about the same rule.
 */
const MIN_YEAR = 1;
const MAX_YEAR = 9999;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Whether these parts name a day that actually exists. */
function isRealCalendarDate({ year, month, day }: CalendarDate): boolean {
  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  if (month < 1 || month > 12) return false;
  const lastDay =
    month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
  return day >= 1 && day <= lastDay;
}

/**
 * Read a stored answer as a calendar date.
 *
 * The `YYYY-MM-DD` fast path is load-bearing: `new Date("2007-01-01")` parses as
 * UTC midnight but `getFullYear()` and friends read local, so anywhere west of
 * UTC the date comes back as the previous day and a rule flips on the boundary.
 *
 * `YYYY-MM-DD` is also the ONLY string shape accepted, because it is the only
 * one the server reads too. V8's legacy parser takes almost anything --
 * "3/5/2019", "March 5, 2019", even "Appendectomy 2015" -> 2015 -- while
 * `_parse_calendar_date` in note_conditions.py takes ISO alone. Falling back to
 * `Date` therefore let an age gate match in the provider's editor and not in the
 * patient portal, on the same stored answer. Both pickers emit `YYYY-MM-DD`, so
 * nothing a user can type through the UI is affected; what changes is that
 * imported or hand-written junk now reads as "not a date" on both surfaces
 * instead of as whatever V8 guessed on one of them.
 */
function parseCalendarDate(value: unknown): CalendarDate | null {
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? null
      : {
          year: value.getFullYear(),
          month: value.getMonth() + 1,
          day: value.getDate(),
        };
  }

  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const iso = ISO_DATE.exec(trimmed);
  if (!iso) return null;

  // The shape is not the date. `2023-02-30` matches this regex but the server's
  // `date.fromisoformat` rejects it and reads the answer as unparseable, so
  // returning it here would let a rule match in the editor that the patient
  // portal treats as unmatched.
  const parts = { year: +iso[1], month: +iso[2], day: +iso[3] };
  return isRealCalendarDate(parts) ? parts : null;
}

/**
 * Whether a stored answer would read as a date here.
 *
 * Exported for the condition editor, which has to decide whether a trigger can
 * ever satisfy a year rule. It asks this rather than shape-matching on its own,
 * because "looks like a date" and "is a date" differ in exactly the cases that
 * matter -- `2023-02-30` and `2019-02-29` are ISO-shaped and neither is a day --
 * and a second implementation would be free to drift from the one that decides
 * what providers actually see.
 */
export function readsAsCalendarDate(value: unknown): boolean {
  return parseCalendarDate(value) !== null;
}

function compareCalendarDates(a: CalendarDate, b: CalendarDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function subtractYears(date: CalendarDate, years: number): CalendarDate {
  const year = date.year - years;
  // Feb 29 has no counterpart in a non-leap threshold year. Clamping to the 28th
  // matches the server; letting Date roll it to Mar 1 would not.
  const day =
    date.month === 2 && date.day === 29 && !isLeapYear(year) ? 28 : date.day;
  return { year, month: date.month, day };
}

function today(): CalendarDate {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
}

function checkYearsAgoCondition(
  valueToCompare: unknown,
  yearsValue: string,
  isMinimum: boolean,
  evaluationDate: string | undefined,
): boolean {
  const answered = parseCalendarDate(valueToCompare);
  if (!answered) {
    return false;
  }

  // parseInt would accept "18abc" as 18; the server's int() rejects it, and the
  // two have to agree. The schema layer already blocks it on write, so this only
  // matters for rules that predate that validation.
  if (!/^-?\d+$/.test(yearsValue.trim())) {
    return false;
  }
  const years = parseInt(yearsValue, 10);

  const threshold = subtractYears(
    parseCalendarDate(evaluationDate) ?? today(),
    years,
  );

  // The server builds this threshold with `date(...)`, which raises for a year
  // outside the calendar; it catches that and reads the rule as unmatched.
  // Comparing numerically here instead would show a question the portal hides.
  // The value is validated on write, so this only catches spans from rules that
  // predate that validation or were written straight to the database.
  if (threshold.year < MIN_YEAR || threshold.year > MAX_YEAR) {
    return false;
  }

  return isMinimum
    ? compareCalendarDates(answered, threshold) <= 0
    : compareCalendarDates(answered, threshold) >= 0;
}

/**
 * Coerce a stored answer into the vocabulary the condition editor writes.
 *
 * TOGGLE answers are booleans but the editor offers "Yes"/"No", so they are
 * compared as strings. Keeping this here (rather than in each caller) is what
 * guarantees forms and notes agree.
 */
export function normalizeConditionValue(
  value: unknown,
  questionType: QuestionType | undefined,
): unknown {
  if (questionType === QuestionType.TOGGLE) {
    return typeof value === "boolean" ? (value ? "Yes" : "No") : value;
  }
  // An unticked checkbox is unanswered, and reads as "No".
  if (questionType === QuestionType.CHECKBOX) {
    return value === true ? "Yes" : "No";
  }
  return value;
}

/**
 * Equality for a condition value, where the answer may be a multi-select array.
 *
 * A multi-select answer is `string[]`, so a plain `===` against the rule's string can
 * never be true -- every EQUALS rule on a multi-select trigger was silently dead, and
 * the gated question never appeared. On an array, EQUALS means "this is the only thing
 * selected", which keeps it meaningfully distinct from CONTAINS ("is among the
 * selections") rather than making the two operators synonyms.
 */
function valuesEqual(answer: unknown, expected: string): boolean {
  if (Array.isArray(answer)) {
    return answer.length === 1 && answer[0] === expected;
  }
  return answer === expected;
}

/**
 * True when every condition passes. No conditions means always visible.
 */
export function evaluateConditions(
  conditions: QuestionCondition[] | undefined | null,
  ctx: ConditionEvalContext,
): boolean {
  if (!conditions || conditions.length === 0) {
    return true;
  }

  return conditions.every((condition) => {
    const questionType = ctx.getQuestionType(condition.dependentQuestionId);

    // Unresolvable means "not one of this surface's questions", regardless of
    // whether the note still holds an answer under that id. The server's values
    // map is derived from the same question list as its types map, so an unknown
    // trigger can never appear in it -- its extra `not in values` check can never
    // fire on its own. Here the two maps have independent keys (answers come from
    // the note, types from the template), so also requiring the value to be absent
    // would evaluate a rule the server skips: a note that kept its answer for a
    // deleted or recreated trigger would have charted content hidden in the
    // editor while the portal still shows it.
    if (ctx.failOpenOnUnknownDependency && questionType === undefined) {
      return true;
    }

    const valueToCompare = normalizeConditionValue(
      ctx.getValue(condition.dependentQuestionId),
      questionType,
    );

    switch (condition.operator) {
      case ConditionOperator.EQUALS:
        return valuesEqual(valueToCompare, condition.value);
      case ConditionOperator.NOT_EQUALS:
        return !valuesEqual(valueToCompare, condition.value);
      case ConditionOperator.CONTAINS:
        // Check the type before reaching for .includes. A NUMBER answer used to
        // reach this line and throw; anything that isn't a string or a list
        // cannot contain the rule's value anyway.
        if (typeof valueToCompare === "string") {
          return valueToCompare.includes(condition.value);
        }
        if (Array.isArray(valueToCompare)) {
          return valueToCompare.includes(condition.value);
        }
        return false;
      case ConditionOperator.YEARS_AGO_MINIMUM:
        return checkYearsAgoCondition(
          valueToCompare,
          condition.value,
          true,
          ctx.evaluationDate,
        );
      case ConditionOperator.YEARS_AGO_MAXIMUM:
        return checkYearsAgoCondition(
          valueToCompare,
          condition.value,
          false,
          ctx.evaluationDate,
        );
      default:
        // Unknown operator (e.g. SUM, which drives calculated scores rather than
        // visibility) must not hide the question.
        return true;
    }
  });
}

/** The shape `computeHiddenIds` needs from a question. */
export interface ConditionalQuestion {
  id: string;
  sectionId?: string | null;
  conditions?: QuestionCondition[] | null;
  /**
   * Archived questions ride along in a note's question list -- see
   * `computeHiddenIds`, which treats one as a trigger that can no longer be
   * resolved. Forms have no archived questions and simply leave this unset.
   */
  isArchived?: boolean | null;
}

/** The shape `computeHiddenIds` needs from a section. */
export interface ConditionalSection {
  id: string;
  conditions?: QuestionCondition[] | null;
}

export interface HiddenIds {
  questionIds: Set<string>;
  sectionIds: Set<string>;
}

/**
 * Everything a note should not show, following rules that chain.
 *
 * Evaluating each rule once is not enough. Given `Tobacco? -> Type -> Packs/day`,
 * answering through the chain and then correcting `Tobacco?` to No hides `Type`
 * -- but `Type` still holds "Cigarettes", so a single pass leaves `Packs/day`
 * on screen under a patient marked as not using tobacco. So a hidden question's
 * answer is treated as absent and the pass repeats until nothing new hides.
 *
 * Sections take part: hiding one hides its questions, whose answers then drop
 * out too, which can hide further sections.
 *
 * Termination: the hidden sets only ever grow, so this runs at most once per
 * owner. It does not depend on the rules being acyclic -- a cycle simply
 * settles with both owners hidden, which is what the editor's ordering rule and
 * the server's cycle check exist to prevent in the first place.
 */
export function computeHiddenIds(
  questions: readonly ConditionalQuestion[],
  sections: readonly ConditionalSection[],
  ctx: ConditionEvalContext,
): HiddenIds {
  const questionIds = new Set<string>();
  const sectionIds = new Set<string>();

  // A note's question list keeps ARCHIVED questions: an old note goes on
  // rendering every question it holds an answer for, active or not (see
  // `_note_questions_to_include` in the notes routes). The server evaluates over
  // the active template only -- `_hidden_for_patient` in
  // `apps/api/routes/patient_portal/notes.py` filters archived questions out
  // before building EITHER of its maps -- so a rule pointing at one is
  // unresolvable there and fails open. Resolving it here would evaluate a rule
  // the server skips, and the same note would render one way for the provider
  // and another in the patient portal.
  const archivedIds = new Set(
    questions.filter((q) => q.isArchived).map((q) => q.id),
  );

  for (;;) {
    const scoped: ConditionEvalContext = {
      ...ctx,
      // A question nobody could see was never answered, whatever is stored.
      // Archived triggers drop out here too, mirroring the server's values map.
      getValue: (id) =>
        questionIds.has(id) || archivedIds.has(id)
          ? undefined
          : ctx.getValue(id),
      // Only an archived trigger is unresolvable. A merely hidden one keeps its
      // type, so this can't trip failOpen -- which is what makes a chained rule
      // hide the rest of its chain instead of being rescued by it.
      getQuestionType: (id) =>
        archivedIds.has(id) ? undefined : ctx.getQuestionType(id),
    };

    let grew = false;

    for (const section of sections) {
      if (sectionIds.has(section.id)) continue;
      if (!evaluateConditions(section.conditions, scoped)) {
        sectionIds.add(section.id);
        grew = true;
      }
    }

    for (const question of questions) {
      if (questionIds.has(question.id)) continue;
      const inHiddenSection =
        !!question.sectionId && sectionIds.has(question.sectionId);
      if (inHiddenSection || !evaluateConditions(question.conditions, scoped)) {
        questionIds.add(question.id);
        grew = true;
      }
    }

    if (!grew) return { questionIds, sectionIds };
  }
}

/**
 * Pull the comparable value out of a note answer.
 *
 * Notes wrap answers in a `data` envelope whose key depends on the question
 * type, whereas forms hold raw values in react-hook-form. This mirrors
 * `extract_answer_value` in `decoda/common/note_text_formatter.py` -- the server
 * unwraps by type, so reading only `values`/`value` here left TAG answers stored
 * under `tags` (and anything stored under `text`) looking unanswered, which
 * silently hid every question gated on one.
 *
 * Returns undefined when there is no answer at all, which is what
 * `failOpenOnUnknownDependency` keys off.
 */
export function noteAnswerValue(
  answer?: { data?: Record<string, any> | null },
  questionType?: QuestionType,
): unknown {
  const data = answer?.data;
  if (!data) return undefined;

  switch (questionType) {
    case QuestionType.MEDICATIONS:
      return data.medications?.length ? data.medications : undefined;
    case QuestionType.TAG:
      // Empty `values` falls through to `tags`, matching the server's `or`.
      return data.values?.length ? data.values : data.tags;
    case QuestionType.MULTI_SELECT:
    case QuestionType.RADIAL_MULTI_SELECT:
      return data.values;
    case undefined:
      // No type to go on -- probe the envelope keys instead. Ordered so the
      // list-shaped keys win, since a question carrying both is multi-valued.
      if (data.values !== undefined) return data.values;
      if (data.tags !== undefined) return data.tags;
      if (data.medications !== undefined) return data.medications;
      return data.value ?? data.text;
    default:
      return data.value ?? data.text;
  }
}

/**
 * Canonical form of a rule set, for comparing two of them.
 *
 * `id` and `questionId` are deliberately excluded: the editor mints fresh ids
 * whenever it rebuilds a question, so comparing them would report every rule as
 * changed. Order is excluded for the same reason -- the rules are ANDed, so a
 * reordering is not a change.
 */
function canonicalConditionKeys(
  conditions: QuestionCondition[] | undefined | null,
): string[] {
  return (conditions ?? [])
    .map((c) =>
      [c.dependentQuestionId, c.operator, c.value, c.groupId ?? ""].join(" "),
    )
    .sort();
}

/** True when two rule sets say the same thing, ignoring ids and ordering. */
export function conditionsEqual(
  a: QuestionCondition[] | undefined | null,
  b: QuestionCondition[] | undefined | null,
): boolean {
  const left = canonicalConditionKeys(a);
  const right = canonicalConditionKeys(b);
  return (
    left.length === right.length && left.every((key, i) => key === right[i])
  );
}
